/**
 * PinKeypad — Plan 01-12 (replaces Plan 11 minimal stub).
 *
 * Kid-first, touch-first 3×4 numeric keypad with:
 *   - MED-6 dot-masking (dot appended synchronously; digit is NEVER shown)
 *   - Keyboard numpad fallback (0-9, Backspace, Enter; letters IGNORED)
 *   - Auto-submit at length match (UI-SPEC §"Interaction Contracts §3")
 *   - Wrong-PIN shake (6px, 300ms) honouring prefers-reduced-motion
 *   - aria-live="polite" error + countdown announcements
 *   - Lockout UX: opacity-50 + pointer-events-none + aria-disabled
 *   - Backwards compatibility with Plan 11 StepMember (testIdPrefix + label
 *     + onChange-only call sites still work — onSubmit is optional)
 *
 * #160: `variant="kitchen"` er Stell_KjokkenPIN — delt oppsett med `lead`
 * og runde prikker til venstre, taster på 112×96 til høyre, «Slett» i
 * stedet for ⌫ og låsingen som banner. Logikken er den samme for begge.
 * #162: kjøkkenet har også «Tøm» i ruta nede til venstre, som i skissen;
 * `trail` står under meldingene (hurtigbyttet «Ikke {navn}?»).
 *
 * Tab order: 1 → 2 → 3 → 4 → 5 → 6 → 7 → 8 → 9 → (Tøm) → 0 → ⌫.  The empty cell
 * (grid position [3, 0]) is aria-hidden + tabIndex=-1 so screen readers
 * and keyboard focus skip it.
 *
 * Backend contract (LoginRoute / Plan 03 POST /api/auth/pin):
 *   - 401 + Retry-After header on lockout → consumer calls
 *     `lockoutSecondsRemaining = usePinLockout.secondsRemaining`
 *   - 401 without Retry-After → consumer passes `errorMessage={t('pin.wrong')}`
 *
 * Letter-spacing exception: the PIN display uses a fixed 0.5em tracking
 * value from UI-SPEC §Typography.  This is the only inline style in the
 * component; see the eslint-disable comment at the render site.
 */
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCountdown } from './hooks/usePinLockout';

const GLYPH_DOT = '●';
const GLYPH_EMPTY_DOT = '·';
const GLYPH_BACKSPACE = '⌫';

type KeyRole = 'digit' | 'empty' | 'back' | 'clear';

interface KeyCell {
  value: string;
  role: KeyRole;
}

/** 3×4 grid layout: 1 2 3 / 4 5 6 / 7 8 9 / empty 0 ⌫ (UI-SPEC §3). */
const KEY_LAYOUT: readonly KeyCell[] = [
  { value: '1', role: 'digit' },
  { value: '2', role: 'digit' },
  { value: '3', role: 'digit' },
  { value: '4', role: 'digit' },
  { value: '5', role: 'digit' },
  { value: '6', role: 'digit' },
  { value: '7', role: 'digit' },
  { value: '8', role: 'digit' },
  { value: '9', role: 'digit' },
  { value: '', role: 'empty' },
  { value: '0', role: 'digit' },
  { value: 'back', role: 'back' },
];

/** Kjøkkenet (#162): Tøm i stedet for den tomme ruta, som i Stell_KjokkenPIN. */
const KITCHEN_KEY_LAYOUT: readonly KeyCell[] = KEY_LAYOUT.map((cell) =>
  cell.role === 'empty' ? { value: 'clear', role: 'clear' } : cell,
);

export interface PinKeypadProps {
  /** PIN length (4–8).  Default 4. */
  length?: number;
  /** Called when the buffer reaches `length` digits (auto-submit). */
  onSubmit?: (pin: string) => void;
  /** Called on every buffer mutation (append or backspace). */
  onChange?: (pin: string) => void;
  /** Admin-disabled state (e.g. request in flight). */
  disabled?: boolean;
  /** If > 0, keypad is locked and the consumer is expected to render the countdown. */
  lockoutSecondsRemaining?: number;
  /** Non-null → wrong-PIN state: display shakes + aria-live announces. */
  errorMessage?: string | null;
  /** Screen-reader label for the keypad group.  Defaults to t('pin.prompt'). */
  ariaLabel?: string;
  /**
   * Plan 11 StepMember compat: prefix for every key's data-testid.  Also
   * used as the top-level group's data-testid so tests can find the keypad
   * root when rendered alongside siblings.
   */
  testIdPrefix?: string;
  /** Plan 11 StepMember compat: explicit visible label.  Wins over ariaLabel. */
  label?: string;
  /**
   * Fang tastetrykk på hele vinduet. Bare for innloggingsskjermen, der
   * tastaturet er alene (#141). I skjemaer lytter tastaturet bare når fokus
   * er inne i det — ellers fyller ett siffer både PIN og bekreft, og
   * passordfeltene mister sifrene sine.
   */
  captureGlobalKeys?: boolean;
  /**
   * `kitchen` er PIN-skjermen på kjøkkenet (Stell_KjokkenPIN, #160): `lead`,
   * prikkene og meldingene til venstre, store taster (112×96) til høyre.
   * `default` er det kompakte tastaturet i skjemaene (AuthFields, galleriet).
   */
  variant?: 'default' | 'kitchen';
  /** Innholdet over prikkene i `kitchen` (avatar, hilsen, ledetekst). */
  lead?: ReactNode;
  /** Innholdet under meldingene i `kitchen` (#162: «Ikke {navn}?»). */
  trail?: ReactNode;
  /**
   * Ferdig låsetekst fra usePinLockout. Hooken vet om låsingen startet lang
   * (≥ 5 min); ut fra sekundene som er igjen ville «spør en voksen» bare stått
   * det første sekundet. Uten den avledes teksten her som før.
   */
  lockoutMessage?: string | null;
}


/** Detects prefers-reduced-motion once at mount; honoured by shake guard. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const LONG_LOCKOUT_THRESHOLD_SECONDS = 300;

export function PinKeypad({
  length = 4,
  onSubmit,
  onChange,
  disabled = false,
  lockoutSecondsRemaining = 0,
  errorMessage = null,
  ariaLabel,
  testIdPrefix = 'pin',
  label,
  captureGlobalKeys = false,
  variant = 'default',
  lead,
  trail,
  lockoutMessage,
}: PinKeypadProps) {
  const kitchen = variant === 'kitchen';
  const { t } = useTranslation();
  const [pin, setPin] = useState<string>('');
  // Track the pin value in a ref so the keyboard-listener closure always
  // reads the current value without re-binding the listener every keystroke.
  const pinRef = useRef<string>('');
  pinRef.current = pin;
  const reducedMotionRef = useRef<boolean>(false);
  reducedMotionRef.current = prefersReducedMotion();
  // Guard against double auto-submit in the same React frame.
  const submittedRef = useRef<boolean>(false);

  const isLocked = disabled || lockoutSecondsRemaining > 0;

  const emit = useCallback(
    (next: string) => {
      setPin(next);
      pinRef.current = next;
      onChange?.(next);
    },
    [onChange],
  );

  const append = useCallback(
    (digit: string) => {
      if (isLocked || submittedRef.current) return;
      const current = pinRef.current;
      if (current.length >= length) return;
      const next = current + digit;
      emit(next);
      if (next.length === length) {
        submittedRef.current = true;
        // Microtask keeps the final dot-mask render flush in front of the
        // consumer's setState (LoginRoute fetch start), preventing an
        // MED-6-adjacent stale "N-1 dots" paint during the POST.
        queueMicrotask(() => {
          onSubmit?.(next);
        });
      }
    },
    [isLocked, length, emit, onSubmit],
  );

  const backspace = useCallback(() => {
    if (isLocked) return;
    submittedRef.current = false;
    const current = pinRef.current;
    if (current.length === 0) return;
    emit(current.slice(0, -1));
  }, [isLocked, emit]);

  // Tøm (#162): hele bufferet på én gang, som Slett fire ganger.
  const clear = useCallback(() => {
    if (isLocked) return;
    submittedRef.current = false;
    if (pinRef.current.length === 0) return;
    emit('');
  }, [isLocked, emit]);

  const submitIfReady = useCallback(() => {
    if (isLocked || submittedRef.current) return;
    const current = pinRef.current;
    if (current.length === length) {
      submittedRef.current = true;
      queueMicrotask(() => {
        onSubmit?.(current);
      });
    }
  }, [isLocked, length, onSubmit]);

  // Keyboard input — numpad digits, Backspace, Enter; letters IGNORED.
  const handleKey = useCallback(
    (e: { key: string; preventDefault: () => void }) => {
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        append(e.key);
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        backspace();
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        submitIfReady();
        return;
      }
      // Letters / symbols ignored (MED-6 + UI-SPEC §3).
    },
    [append, backspace, submitIfReady],
  );

  // Global listener only on the login screen (captureGlobalKeys); in forms
  // the group's onKeyDown below handles keys while focus is inside it.
  useEffect(() => {
    if (!captureGlobalKeys) return;
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [captureGlobalKeys, handleKey]);

  // When an errorMessage arrives (wrong PIN response from the server),
  // clear the buffer so the user can retype without pressing ⌫ four times.
  useEffect(() => {
    if (errorMessage) {
      submittedRef.current = false;
      setPin('');
      pinRef.current = '';
    }
  }, [errorMessage]);

  // Build the dot-mask string: filled dots for typed digits, empty dots
  // for the remaining slots.  MED-6: never echo the raw digit.
  const displayDots =
    GLYPH_DOT.repeat(pin.length) + GLYPH_EMPTY_DOT.repeat(Math.max(0, length - pin.length));

  const resolvedAriaLabel = label ?? ariaLabel ?? t('pin.prompt');
  const shakeClass =
    errorMessage && !reducedMotionRef.current ? 'animate-shake' : '';

  function press(cell: KeyCell): void {
    if (cell.role === 'back') backspace();
    else if (cell.role === 'clear') clear();
    else append(cell.value);
  }

  function onKeyButtonKeyDown(
    e: ReactKeyboardEvent<HTMLButtonElement>,
    cell: KeyCell,
  ) {
    // Space/Enter on a focused digit key should activate that key (default
    // <button type="button"> behaviour covers Enter; handle Space explicitly
    // so Tab-and-Space is frictionless for keyboard users).
    if (e.key === ' ') {
      e.preventDefault();
      press(cell);
    }
  }

  // Kjøkkenet viser fire runde prikker i stedet for glyfer, og skjermleseren
  // får «2 av 4 sifre tastet» i stedet for prikkmønsteret (MED-6: aldri sifrene).
  const display = kitchen ? (
    <div
      data-testid={`${testIdPrefix}-display`}
      className={`flex h-6.5 gap-4.5 ${shakeClass}`}
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="sr-only">{t('pin.dots_label', { count: pin.length, length })}</span>
      {Array.from({ length }, (_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`h-6.5 w-6.5 rounded-full border-3 ${i < pin.length ? 'border-lime bg-lime' : 'border-track'}`}
        />
      ))}
    </div>
  ) : (
    <div
      data-testid={`${testIdPrefix}-display`}
      className={`font-display text-display-s text-ink font-extrabold flex h-16 items-center justify-center ${shakeClass}`}
      aria-live="polite"
      aria-atomic="true"
    >
      {/* PIN display letter-spacing is a locked UI-SPEC value (§Typography).
          The rule set does not flag inline style objects, so no
          eslint-disable directive is needed — keeping the constant
          inline is simpler than adding a one-off utility class. */}
      <span style={{ letterSpacing: '0.5em' }}>{displayDots}</span>
    </div>
  );

  // Wrong-PIN helper text (polite announce).  Separate from the
  // display so screen readers read the message, not the dot pattern.
  const error = errorMessage ? (
    <p
      data-testid={`${testIdPrefix}-error`}
      className={kitchen ? 'text-bad m-0 text-body font-bold' : 'text-bad text-sm'}
      aria-live="polite"
    >
      {errorMessage}
    </p>
  ) : null;

  // Lockout countdown — consumer-driven. På kjøkkenet et gult banner
  // (Stell_KjokkenPIN, #160); teksten er den samme begge steder.
  const lockoutText =
    lockoutMessage ??
    t(
      lockoutSecondsRemaining >= LONG_LOCKOUT_THRESHOLD_SECONDS ? 'pin.lockout_long' : 'pin.lockout',
      { time: formatCountdown(lockoutSecondsRemaining) },
    );
  const lockout =
    lockoutSecondsRemaining > 0 ? (
      <p
        data-testid={`${testIdPrefix}-lockout-announce`}
        className={
          kitchen
            ? 'bg-warn-bg text-ink tnum m-0 rounded-md px-4.5 py-3.5 text-body font-bold'
            : 'text-bad tnum font-mono text-sm'
        }
        aria-live="polite"
        aria-atomic="true"
      >
        {lockoutText}
      </p>
    ) : null;

  // 3×4 key grid. Skjema: 80×80. Kjøkken: 112×96 med radius 28 (skissen),
  // smalere på telefonbredde så tre taster får plass. Kjøkkenet har Tøm i
  // nederste venstre rute (#162); skjemaene har ruta tom.
  const keyClass = kitchen
    ? 'h-20 w-full md:h-24 rounded-xl text-4xl'
    : 'h-20 w-20 rounded-xl text-3xl';
  const keys = (
    <div
      className={[
        kitchen ? 'grid w-full max-w-92 grid-cols-3 gap-3 md:gap-4' : 'grid grid-cols-3 gap-2',
        isLocked ? 'pointer-events-none opacity-50' : '',
      ].join(' ')}
    >
      {(kitchen ? KITCHEN_KEY_LAYOUT : KEY_LAYOUT).map((cell, i) => {
        if (cell.role === 'empty') {
          return (
            <div
              key={`empty-${i}`}
              aria-hidden="true"
              tabIndex={-1}
              className={kitchen ? 'h-20 md:h-24' : 'h-20 w-20'}
            />
          );
        }
        const { testId, ariaLabel: keyAria, text } =
          cell.role === 'back'
            ? {
                testId: `${testIdPrefix}-backspace`,
                ariaLabel: t(kitchen ? 'pin.delete_aria' : 'common.remove'),
                text: kitchen ? t('pin.delete') : GLYPH_BACKSPACE,
              }
            : cell.role === 'clear'
              ? { testId: `${testIdPrefix}-clear`, ariaLabel: t('pin.clear_aria'), text: t('pin.clear') }
              : { testId: `${testIdPrefix}-key-${cell.value}`, ariaLabel: cell.value, text: cell.value };
        const word = cell.role !== 'digit';
        return (
          <button
            key={`${cell.role}-${cell.value}-${i}`}
            type="button"
            data-testid={testId}
            className={`bg-card border-card-line text-ink hover:border-secondary-line flex items-center justify-center border font-extrabold backdrop-blur-card ${keyClass} ${
              word && kitchen ? 'text-lg font-bold' : ''
            }`}
            aria-disabled={isLocked}
            aria-label={keyAria}
            onClick={() => press(cell)}
            onKeyDown={(e) => onKeyButtonKeyDown(e, cell)}
          >
            {text}
          </button>
        );
      })}
    </div>
  );

  // Forgot-PIN ghost link — routes to admin re-auth flow in Phase 6;
  // Phase 1 scope covers the link surface only.
  const forgot = (
    <a
      data-testid={`${testIdPrefix}-forgot`}
      href="/login?reset=forgot"
      className={
        kitchen
          ? 'text-ink-2 flex min-h-11 items-center text-body font-semibold underline'
          : 'text-ink-2 text-sm underline'
      }
    >
      {t('pin.forgot')}
    </a>
  );

  return (
    <div
      data-testid={testIdPrefix}
      className={
        kitchen
          ? 'flex w-full flex-col items-center gap-10 md:flex-row md:items-start md:justify-between'
          : 'flex flex-col items-center gap-6'
      }
      role="group"
      onKeyDown={captureGlobalKeys ? undefined : handleKey}
      aria-label={resolvedAriaLabel}
    >
      {kitchen ? (
        <>
          <div className="flex w-full max-w-115 flex-col items-start gap-5.5">
            {lead}
            {display}
            {/* Banneret sier alt mens tastaturet er låst; «Feil PIN» i tillegg
                ble dobbelt. */}
            {lockout ?? error}
            {forgot}
            {trail}
          </div>
          {keys}
        </>
      ) : (
        <>
          {display}
          {error}
          {lockout}
          {keys}
          {forgot}
        </>
      )}
    </div>
  );
}
